import { Functions } from "appwrite";
import { client, account } from "./appwrite.js";
import { toUserFacingBillingError } from "./userFacingErrors.js";

const functions = new Functions(client);

const JWT_CACHE_MS = 4 * 60 * 1000;
let jwtCache = { token: null, expiresAt: 0 };

export function clearGatewayJwtCache() {
  jwtCache = { token: null, expiresAt: 0 };
}

function stripeGatewayFunctionId() {
  const id =
    import.meta.env.VITE_APPWRITE_STRIPE_GATEWAY_ID?.trim() ||
    import.meta.env.VITE_APPWRITE_FUNCTION_CHECKOUT_ID?.trim() ||
    "";
  if (!id) {
    throw new Error("Set VITE_APPWRITE_STRIPE_GATEWAY_ID to your Appwrite function ID.");
  }
  return id;
}

async function jwtExecutionHeaders() {
  const now = Date.now();
  if (jwtCache.token && jwtCache.expiresAt > now) {
    return { authorization: `Bearer ${jwtCache.token}` };
  }

  try {
    const { jwt } = await account.createJWT();
    if (typeof jwt === "string" && jwt) {
      jwtCache = { token: jwt, expiresAt: now + JWT_CACHE_MS };
    }

    return { authorization: `Bearer ${jwt}` };
  } catch (e) {
    const msg = String(e?.message ?? e ?? "");
    const jwtDisabled =
      e?.code === 501 ||
      msg.includes("JWT authentication is disabled") ||
      msg.includes("501");

    if (jwtDisabled) {
      return {};
    }
    throw e;
  }
}

async function callGateway(path, body = {}) {
  const functionId = stripeGatewayFunctionId();
  const payload = { ...body, siteOrigin: window.location.origin };

  const extraHeaders = await jwtExecutionHeaders();

  let execution;
  try {
    execution = await functions.createExecution({
      functionId,
      body: JSON.stringify(payload),
      async: false,
      xpath: path,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...extraHeaders,
      },
    });
  } catch (e) {
    const code = e?.code;
    const type = e?.type;
    const raw = typeof e?.response === "string" ? e.response : "";
    let detail = "";
    if (raw) {
      try {
        const j = JSON.parse(raw);
        detail = j.message ?? j.error ?? raw;
      } catch {
        detail = raw.slice(0, 800);
      }
    }
    const technical = `Appwrite: ${e?.message ?? e}${detail ? ` — ${detail}` : ""}${code != null ? ` (${code})` : ""}${type ? ` [${type}]` : ""}`;
    throw new Error(toUserFacingBillingError(technical));
  }

  let data;
  try {
    data = JSON.parse(execution.responseBody || "{}");
  } catch {
    const technical = `Non-JSON response (${execution.responseStatusCode}): ${String(execution.responseBody).slice(0, 180)}`;
    throw new Error(toUserFacingBillingError(technical));
  }

  if (execution.responseStatusCode >= 400) {
    const technical = data.error ?? `HTTP ${execution.responseStatusCode}`;
    throw new Error(toUserFacingBillingError(technical));
  }

  return data;
}

export async function createCheckoutSession(planId, interval, opts = {}) {
  return callGateway("/checkout", {
    action: "checkout",
    planId,
    interval,
    ...(opts.code ? { code: opts.code } : {}),
  });
}

export async function createListingCheckoutSession(listingId) {
  return callGateway("/", { action: "checkout-listing", listingId });
}

export async function fetchListingAccess(listingId) {
  return callGateway("/", { action: "listing-access", listingId });
}

export async function syncListingCheckoutSession(sessionId, listingId) {
  return callGateway("/", {
    action: "sync-listing-checkout",
    sessionId,
    listingId,
  });
}

export async function syncListingCheckoutSessionWithRetry(sessionId, listingId, opts = {}) {
  const retries = opts.retries ?? 5;
  const baseDelayMs = opts.baseDelayMs ?? 900;
  const sid = String(sessionId ?? "").trim();
  const lid = String(listingId ?? "").trim();
  if (!sid) throw new Error("Missing Stripe Checkout session id.");
  if (!lid) throw new Error("Missing listing id.");
  let lastErr;
  for (let i = 0; i < retries; i++) {
    try {
      return await syncListingCheckoutSession(sid, lid);
    } catch (e) {
      lastErr = e;
      if (i < retries - 1) {
        await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
      }
    }
  }
  throw lastErr ?? new Error("Could not verify listing purchase.");
}

export async function cancelListingSubscription(listingId) {
  return callGateway("/", { action: "cancel-listing-subscription", listingId });
}

export async function updateListingPricing(listingId, { price, pricingMode }) {
  return callGateway("/", {
    action: "update-listing-pricing",
    listingId,
    price,
    pricingMode,
  });
}

export async function createCustomerPortalSession() {
  return callGateway("/portal", { action: "portal" });
}

export async function createClientPortalSession() {
  return callGateway("/", { action: "client-portal" });
}

export async function fetchSubscriptionViaGateway() {
  return callGateway("/", { action: "subscription-summary" });
}

export async function fetchInvoicesViaGateway(limit = 50) {
  const n = Math.min(100, Math.max(1, Math.floor(Number(limit) || 50)));
  return callGateway("/", { action: "list-invoices", limit: n });
}

export async function fetchPlansViaGateway(opts = {}) {
  return callGateway("/", {
    action: "list-plans",
    ...(opts.audience ? { audience: opts.audience } : {}),
  });
}

export async function syncCheckoutSessionFromStripe(sessionId) {
  return callGateway("/", {
    action: "sync-checkout-session",
    sessionId,
  });
}

export async function syncCheckoutSessionFromStripeWithRetry(sessionId, opts = {}) {
  const retries = opts.retries ?? 4;
  const baseDelayMs = opts.baseDelayMs ?? 900;
  const id = String(sessionId ?? "").trim();
  if (!id) throw new Error("Missing Stripe Checkout session id.");
  let lastErr;
  for (let i = 0; i < retries; i++) {
    try {
      return await syncCheckoutSessionFromStripe(id);
    } catch (e) {
      lastErr = e;
      if (i < retries - 1) {
        await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
      }
    }
  }
  throw lastErr;
}

export async function fetchConnectStatus() {
  return callGateway("/", { action: "connect-status" });
}

export async function startConnectOnboarding() {
  return callGateway("/", { action: "connect-onboard" });
}

export async function openConnectDashboard() {
  return callGateway("/", { action: "connect-dashboard" });
}

export async function fetchCoachClientPlans(opts = {}) {
  return callGateway("/", {
    action: "list-coach-plans",
    ...(opts.includeArchived ? { includeArchived: true } : {}),
    ...(opts.coachId ? { coachId: opts.coachId } : {}),
  });
}

export async function createCoachClientPlan(plan) {
  return callGateway("/", { action: "create-coach-plan", ...plan });
}

export async function archiveCoachClientPlan(planId) {
  return callGateway("/", { action: "archive-coach-plan", planId });
}

export async function checkoutCoachClientPlan(planId) {
  return callGateway("/", { action: "checkout-coach-plan", planId });
}

export async function syncCoachCheckoutSessionFromStripe(sessionId) {
  return callGateway("/", {
    action: "sync-coach-checkout-session",
    sessionId,
  });
}

export async function syncCoachCheckoutSessionFromStripeWithRetry(sessionId, opts = {}) {
  const retries = opts.retries ?? 4;
  const baseDelayMs = opts.baseDelayMs ?? 900;
  const id = String(sessionId ?? "").trim();
  if (!id) throw new Error("Missing Stripe Checkout session id.");
  let lastErr;
  for (let i = 0; i < retries; i++) {
    try {
      const out = await syncCoachCheckoutSessionFromStripe(id);
      return out;
    } catch (e) {
      lastErr = e;
      if (i < retries - 1) {
        await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
      }
    }
  }
  throw lastErr;
}

export async function fetchClientInvoicesViaGateway(limit = 50) {
  const n = Math.min(100, Math.max(1, Math.floor(Number(limit) || 50)));
  return callGateway("/", { action: "list-client-invoices", limit: n });
}

export async function fetchOwnedListingsViaGateway() {
  return callGateway("/", { action: "list-owned-listings" });
}

export async function fetchClientCoachAccess() {
  return callGateway("/", { action: "client-coach-access" });
}

export async function fetchCoachConnectSummary() {
  return callGateway("/", { action: "coach-connect-summary" });
}

export async function fetchCoachClientBilling() {
  return callGateway("/", { action: "coach-client-billing" });
}
